import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClaimSubmissionRequest,
  ClaimSubmissionResponse,
  useClaimUpdate,
  useDocumentsDelete,
  useViewClaimDetails,
  ViewClaimDetailsResponse,
} from 'all-in-one-shared-api';

import { GenericResult, Spinner } from 'components';
import { isLocaleEnglish } from 'utils/languageUtils';
import { getClaimPageUrl } from 'utils';
import { MessageType } from 'models/MessageType';
import { formatClaimSubmissionRequest, toClaimFormState } from './utils';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelectedClaimTypeMapping } from '../../../newClaim/hooks/useSelectedClaimTypeMapping';
import { useKeycloakReady } from '../../../../hooks';
import ClaimInfoCard from '../../../newClaim/components/ClaimInfoCard/ClaimInfoCard';
import { ClaimTabs } from '../../../../components/claimTab/ClaimTab';
import styled from 'styled-components';
import { ClaimFormState, excludeRemovedDocuments, getClaimSchema } from '../../../newClaim/schema';
import { PartialClaimCard } from '../../../newClaim/components/PartialClaimCard';
import ClaimAmountCard from '../../../newClaim/components/ClaimAmountCard';
import DocumentsCard, {
  getNeededRemoveDocuments,
  getUploadDocumentParams,
} from '../../../newClaim/components/DocumentsCard';
import { ClaimDetailTitle } from '../../../newClaim/components/ClaimDetailTitle';
import { UpdateClaimButtonGroup } from '../index';
import { getClaimSubmissionRequest } from '../../../newClaim/utils';
import { useUploadFiles } from '../../../../api/fileManager';
import { FileUploaded } from '../../../../components/form';
import { revalidateReactFormHook } from '../../../../i18n';
import ClaimDetailsModal from '../ClaimDetailsModal';
interface EditClaimFormProps {
  claimId: string;
  sequenceNo?: number;
}

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 200px;
  row-gap: 32px;
  column-gap: 18px;

  .ant-form-item-required::before {
    display: none !important;
  }
`;

const FromWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

export enum SubmitProcess {
  FORM_SUBMIT = 'formSubmit',
  UPLOAD_FILES = 'uploadFiles',
  DELETE_FILES = 'deleteFiles',
}

export const EditClaimForm = ({ claimId, sequenceNo }: EditClaimFormProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { ready, token } = useKeycloakReady();
  const navigate = useNavigate();
  const schema = useMemo(() => getClaimSchema(t, i18n.language), [t, i18n.language]);
  const methods = useForm<ClaimFormState>({
    resolver: yupResolver(schema),
    defaultValues: {
      claimType: '',
      receipts: [],
      referralLetters: [],
      otherDocuments: [],
      prevClaimSettlements: [],
      partialClaim: false,
      patientMemberType: undefined,
    },
  });

  useEffect(() => {
    revalidateReactFormHook(methods);
  }, [i18n.language, methods]);

  const { selectedClaimTypeObject } = useSelectedClaimTypeMapping({ methods });
  const [formData, setFormData] = useState<ClaimSubmissionRequest>();
  const [claimDetailsPageUrl, setClaimDetailsPageUrl] = useState<string>('');
  const [deletedFileNames, setDeletedFileNames] = useState<string>('');
  const submitProcess = useRef<SubmitProcess[]>([]);
  const claimSubmissionResponseRef = useRef<ClaimSubmissionResponse>();
  const [showModal, setShowModal] = useState(false);

  const { shouldFetch, isLoading, error } = useViewClaimDetails(
    { name: 'useViewClaimDetails', params: { claimId } },
    token || '',
    claimId,
    sequenceNo,
    {
      onSuccess: (data: ViewClaimDetailsResponse) => {
        const initialData = toClaimFormState(data, isLocaleEnglish(i18n.language));
        if (!initialData.editAllowed && !data) {
          navigate(-1);
        }
        methods.reset(initialData);
        shouldFetch(false);

        setClaimDetailsPageUrl(getClaimPageUrl(data) || '');
      },
    },
  );

  const updateParams = formatClaimSubmissionRequest(claimId, formData);

  const navigateAfterSubmit = (action: SubmitProcess, isSucceed = false) => {
    if (submitProcess === undefined) return;
    const excludeProcessFinished = submitProcess.current.filter((process) => process !== action);
    submitProcess.current = excludeProcessFinished;

    if (excludeProcessFinished.length !== 0 && isSucceed) return;

    let state = {
      messageType: MessageType.ERROR,
      messageContent: t('editClaim.editError'),
    };
    if (isSucceed) {
      state = {
        messageType: MessageType.SUCCESS,
        messageContent: t('editClaim.editSuccess'),
      };
    }

    if (claimSubmissionResponseRef.current?.receiptWaiverAmount) {
      navigate('/claims/edit-success', {
        state: {
          referenceNo: claimSubmissionResponseRef.current?.referenceNo,
          remark: claimSubmissionResponseRef.current?.remark,
          receiptWaiverAmount: claimSubmissionResponseRef.current?.receiptWaiverAmount,
        },
        replace: true,
      });
    } else {
      setTimeout(() => {
        navigate(claimDetailsPageUrl, {
          state: state,
          replace: true,
        });
      }, 1000);
    }
  };

  const { shouldFetch: shouldFetchUpdateClaim } = useClaimUpdate(
    { name: 'useClaimUpdate', params: updateParams },
    token || '',
    updateParams,
    false,
    {
      onSuccess: (claimSubmissionResponse) => {
        claimSubmissionResponseRef.current = claimSubmissionResponse;
        navigateAfterSubmit(SubmitProcess.FORM_SUBMIT, true);
        shouldFetchUpdateClaim(false);
      },
      onError: (error) => {
        if (error.code === 'dapi.claim.not.allow.operate') {
          setShowModal(true);
        } else {
          navigateAfterSubmit(SubmitProcess.FORM_SUBMIT);
        }
        shouldFetchUpdateClaim(false);
      },
    },
  );

  const getUploadFilesParams = () => {
    const data = methods.getValues();
    return {
      formData: methods.getValues(),
      claimId: data.referenceNo || '',
      treatmentDate: data.treatmentDate || '',
      ...getUploadDocumentParams(data),
    };
  };

  const { shouldFetch: uploadDocumentsShouldFetch } = useUploadFiles(
    { name: 'useUploadFiles', params: getUploadFilesParams() },
    token || '',
    getUploadFilesParams(),
    {
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      revalidateOnFocus: false,
      onSuccess: () => {
        uploadDocumentsShouldFetch(false);
        navigateAfterSubmit(SubmitProcess.UPLOAD_FILES, true);
      },
      onError: (error) => {
        uploadDocumentsShouldFetch(false);
        if (error.code === 'dapi.claim.not.allow.operate') {
          setShowModal(true);
        } else {
          navigateAfterSubmit(SubmitProcess.UPLOAD_FILES);
        }
      },
    },
  );

  const { shouldFetch: deleteDocumentsShouldFetch } = useDocumentsDelete(
    { name: 'deleteFiles' },
    token || '',
    methods.getValues('referenceNo') || '',
    deletedFileNames,
    {
      onSuccess: () => {
        deleteDocumentsShouldFetch(false);
        uploadDocumentsShouldFetch(true);
        navigateAfterSubmit(SubmitProcess.DELETE_FILES, true);
      },
      onError: (error) => {
        deleteDocumentsShouldFetch(false);
        if (error.code === 'dapi.claim.not.allow.operate') {
          setShowModal(true);
        } else {
          navigateAfterSubmit(SubmitProcess.DELETE_FILES);
        }
      },
    },
  );

  useEffect(() => {
    shouldFetch(ready);
  }, [ready, shouldFetch]);

  const claimTabs = [
    { translationKey: 'newClaim.claimInfo.title', key: 'claimInformation' },
    { translationKey: 'newClaim.partialClaim.title', key: 'partialClaim' },
    { translationKey: 'newClaim.paymentInfo.receiptInfo', key: 'claimAmount' },
    { translationKey: 'newClaim.supplementaryDocuments.title', key: 'documents' },
  ];

  const checkDeleteAndUploadFiles = (values: ClaimFormState) => {
    const deletedFileName = getNeededRemoveDocuments(values);
    if (deletedFileName) {
      submitProcess.current = [...submitProcess.current, SubmitProcess.DELETE_FILES, SubmitProcess.UPLOAD_FILES];
      setDeletedFileNames(deletedFileName);
      deleteDocumentsShouldFetch(true);
    } else {
      const excludedRemovedDocuments = excludeRemovedDocuments([
        ...(values.receipts || []),
        ...(values.referralLetters || []),
        ...(values.otherDocuments || []),
        ...(values.prevClaimSettlements || []),
      ]);
      const neededUploadFiles = excludedRemovedDocuments.filter((file: FileUploaded) => file.type);
      const hasNewDocuments = neededUploadFiles.length > 0;
      if (hasNewDocuments) {
        submitProcess.current = [...submitProcess.current, SubmitProcess.UPLOAD_FILES];
        uploadDocumentsShouldFetch(true);
      }
    }
  };

  const onSubmit = (values: ClaimFormState) => {
    checkDeleteAndUploadFiles(values);
    setFormData(getClaimSubmissionRequest({ ...values }));
    shouldFetchUpdateClaim(true);
    submitProcess.current = [...submitProcess.current, SubmitProcess.FORM_SUBMIT];
  };

  if (isLoading || !selectedClaimTypeObject.type) {
    return <Spinner />;
  }
  if (error) {
    return (
      <GenericResult
        status="error"
        title={t('claims.notFound.title')}
        defaultTo="/claims"
        defaultLabel={t('claims.notFound.goBack')}
      />
    );
  }
  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Wrapper>
            <ClaimDetailTitle title={'claimDetails.editClaim'} />
            <>
              <FromWrapper>
                <ClaimInfoCard isEditClaim={true} selectedClaimTypeObject={selectedClaimTypeObject} />
                <PartialClaimCard />
                <ClaimAmountCard selectedClaimTypeObject={selectedClaimTypeObject} />
                <DocumentsCard selectedClaimTypeObject={selectedClaimTypeObject} allowRemoveDocuments={true} />
                <UpdateClaimButtonGroup submitProcess={submitProcess} />
              </FromWrapper>
              <ClaimTabs claimTabs={claimTabs} />
            </>
          </Wrapper>
        </form>
      </FormProvider>
      <ClaimDetailsModal
        handleOk={() => setShowModal(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
        visible={showModal}
        setVisibility={setShowModal}
        modalLoading={false}
        title={t('claimDetails.editErrorTitle')}
        description={t('claimDetails.editError')}
      />
    </>
  );
};
